/** @jsxImportSource @emotion/react */
import { RiBarcodeLine, RiAlertLine } from 'react-icons/ri';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { theme } from '@omnigenbiodata/ui';
import { useStyles } from './component.styles';
import Box from '@mui/material/Box';
import { AMGEN_DOT_COLOUR, MESSAGES } from '../../../../../../../../core/constants/forms.constants';
import { SamplePosition } from '../../../../../../../../core/types/common.types';
import { FormikErrors } from 'formik';

interface ScannedCellProps {
  hasError?: string | FormikErrors<SamplePosition>;
  onSelect: (index: number) => void;
  onRemove: (index: number) => void;
  position: number;
  label: string;
  value?: SamplePosition;
  editMode: boolean;
  sequencer?: string;
}

function ScannedCell({ hasError, onSelect, position, label, value, editMode, sequencer }: ScannedCellProps) {
  const classes = useStyles();
  let dotColor = '';
  const _sequencer = value?.sequencer || sequencer || 'MIXED';
  if (_sequencer === 'REGENERON') {
    dotColor = theme.palette.info.main;
  } else if (_sequencer === 'AMGEN') {
    dotColor = AMGEN_DOT_COLOUR;
  } else {
    dotColor = theme.palette.grey[600];
  }
  return (
    <div
      key={`scanned-cell-${position}`}
      data-testid={`scanned-cell-${position}`}
      css={[
        classes.sampleColumn,
        !value && !hasError && classes.sampleColumnEmpty,
        value && !hasError && classes.sampleColumnFull,
        (hasError || value?.participantWithdrawn) && classes.sampleColumnError,
        (editMode || value) && classes.isSelectable,
      ]}
      onClick={() => {
        if (editMode || value) {
          onSelect(position);
        }
      }}
    >
      {dotColor && value && (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: dotColor,
            marginLeft: 5,
            marginTop: '6px',
            border: '1px solid white',
          }}
        />
      )}
      <div css={classes.sampleIndex}>{label}</div>
      <div css={classes.sampleContent}>
        {value && (
          <Tooltip
            arrow
            title={
              <Box p={2} alignItems="middle">
                <Typography variant="body1" component="span" css={classes.tooltipTypog}>
                  {_sequencer}
                </Typography>
                <Typography variant="body1" component="span" css={classes.tooltipTypog}>
                  {!hasError && <RiBarcodeLine size={25} css={classes.tooltipIcon} />}
                  {hasError && <RiAlertLine size={25} css={classes.tooltipIcon} />}{' '}
                  {hasError ? MESSAGES.barcodeInvalid : value.sampleBarcode}
                </Typography>
                {value.participantWithdrawn && (
                  <Typography variant="body1" component="span" justifyContent="center" css={classes.tooltipTypog}>
                    WITHDRAWN
                  </Typography>
                )}
              </Box>
            }
            aria-label={value.sampleBarcode}
            data-testid={`sample-${value.sampleBarcode}`}
            placement="top"
          >
            <div>
              <IconButton aria-label="Valid Sample" size="small">
                <RiBarcodeLine
                  color={theme.palette.common.white}
                  title={value.sampleBarcode}
                  fontSize={30}
                  alignmentBaseline="middle"
                />
              </IconButton>
            </div>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

export default ScannedCell;
